import { useEffect, useRef, useState } from "react";

function loadMuxPlayerScript() {
  if (typeof document === "undefined") return Promise.resolve();
  if (customElements.get("mux-player")) return Promise.resolve();

  return new Promise((resolve) => {
    const existing = document.querySelector('script[data-mux-player]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      if (customElements.get("mux-player")) resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/@mux/mux-player";
    script.type = "module";
    script.dataset.muxPlayer = "true";
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function PhotoZoom({ src, alt, locked }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const pinchRef = useRef({ dist: 0, scale: 1 });

  const onTouchStart = (e) => {
    if (locked || e.touches.length !== 2) return;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    pinchRef.current = { dist: Math.hypot(dx, dy), scale };
  };

  const onTouchMove = (e) => {
    if (locked || e.touches.length !== 2) return;
    e.preventDefault();
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.hypot(dx, dy);
    if (!pinchRef.current.dist) return;
    const next = Math.min(4, Math.max(1, pinchRef.current.scale * (dist / pinchRef.current.dist)));
    setScale(next);
  };

  const onTouchEnd = () => {
    if (scale <= 1.05) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  };

  return (
    <div
      ref={containerRef}
      className="dv-media-photo"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <img
        src={src}
        alt={alt}
        className={`dv-media-photo-img${locked ? " is-locked" : ""}`}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
        draggable={false}
      />
    </div>
  );
}

function AudioPlayer({ src, avatarUrl, creatorName, locked }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const bars = useRef(
    Array.from({ length: 36 }, (_, i) => 0.28 + ((i * 37) % 53) / 80)
  ).current;

  const toggle = () => {
    if (locked || !audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying(!playing);
  };

  return (
    <div className="dv-media-audio">
      <div className="dv-media-audio-stage">
        <img src={avatarUrl} alt={creatorName} className="dv-media-audio-avatar" />
        <div className={`dv-media-waveform${playing ? " is-playing" : ""}`} aria-hidden>
          {bars.map((h, i) => (
            <span
              key={i}
              style={{
                height: `${Math.round(h * 100)}%`,
                animationDelay: `${i * 0.04}s`,
              }}
            />
          ))}
        </div>
      </div>

      {!locked && src && (
        <>
          <audio
            ref={audioRef}
            src={src}
            preload="metadata"
            onLoadedMetadata={() => {
              if (audioRef.current?.duration) setDuration(audioRef.current.duration);
            }}
            onTimeUpdate={() => {
              if (!audioRef.current?.duration) return;
              setCurrent(audioRef.current.currentTime);
              setProgress(audioRef.current.currentTime / audioRef.current.duration);
            }}
            onEnded={() => setPlaying(false)}
          />
          <div className="dv-media-audio-controls">
            <button type="button" className="dv-media-audio-btn" onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
              {playing ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>
              )}
            </button>
            <span className="dv-media-audio-time">{formatTime(current)}</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={progress}
              aria-label="Scrub audio"
              onChange={(e) => {
                if (!audioRef.current?.duration) return;
                const val = Number(e.target.value);
                audioRef.current.currentTime = val * audioRef.current.duration;
                setProgress(val);
              }}
              className="dv-media-audio-scrub"
            />
            <span className="dv-media-audio-time">{formatTime(duration)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function articleTeaserLines(text) {
  return (text || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean)
    .slice(0, 3);
}

function ArticleTeaser({ text, locked }) {
  const lines = articleTeaserLines(text);

  return (
    <div className="dv-media-article">
      <div className="dv-media-article-inner">
        {lines.length ? (
          lines.map((line, i) => (
            <p key={i} className="dv-media-article-line">{line}</p>
          ))
        ) : (
          <p className="dv-media-article-line">A written drop awaits inside.</p>
        )}
        {locked && <span className="dv-media-article-hand">unlock to read the full piece</span>}
        {!locked && text && articleTeaserLines(text).join(" ").length < text.trim().length && (
          <span className="dv-media-article-hand">full text below</span>
        )}
      </div>
    </div>
  );
}

export default function DropMedia({
  drop,
  creator,
  locked,
  compact = false,
}) {
  const [muxReady, setMuxReady] = useState(false);
  const contentType = drop?.contentType || "video";
  const playbackId = drop?.muxPlaybackId || drop?.playbackId || null;
  const thumb = drop?.thumbnailUrl || drop?.mediaUrl || "";
  const avatar = creator?.avatarUrl || creator?.avatar || thumb;
  const articleText = drop?.mediaUrl || drop?.articleBody || drop?.description || drop?.subtitle || "";

  useEffect(() => {
    if (contentType !== "video" || !playbackId) return;
    loadMuxPlayerScript().then(() => setMuxReady(true));
  }, [contentType, playbackId]);

  if (contentType === "video") {
    if (playbackId && muxReady && !locked) {
      return (
        <div className={`dv-media-video${compact ? " is-compact" : ""}`}>
          <mux-player
            playback-id={playbackId}
            stream-type="on-demand"
            accent-color="#C4790A"
            style={{ width: "100%", height: "100%", "--controls": "none" }}
          />
        </div>
      );
    }

    return (
      <div className={`dv-media-video dv-media-video--placeholder${compact ? " is-compact" : ""}`}>
        {thumb && <img src={thumb} alt="" className="dv-media-video-thumb" />}
        <div className="dv-media-video-overlay">
          <div className="dv-media-play-btn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
          <span className="dv-media-video-label">
            {playbackId ? "Video ready" : "Processing your video…"}
          </span>
        </div>
      </div>
    );
  }

  if (contentType === "photo") {
    return (
      <PhotoZoom
        src={thumb}
        alt={drop?.title || "Photo drop"}
        locked={locked}
      />
    );
  }

  if (contentType === "audio") {
    return (
      <AudioPlayer
        src={locked ? null : drop?.mediaUrl}
        avatarUrl={avatar}
        creatorName={creator?.displayName || "Creator"}
        locked={locked}
      />
    );
  }

  if (contentType === "article") {
    return (
      <ArticleTeaser
        text={articleText}
        locked={locked}
      />
    );
  }

  return (
    <div className="dv-media-fallback">
      {thumb ? <img src={thumb} alt={drop?.title || "Drop"} /> : <span>Drop media</span>}
    </div>
  );
}
